<template>
  <div class="signup-container">
    <MainNavbar />

    <div class="center-wrapper">
      <div class="signup-card">
        <h2 class="title">Become a Seller</h2>
        <p class="subtitle">Start selling your sports products on IPA Marketplace</p>

        <form class="signup-form" @submit.prevent="openPreview">
          <!-- FULL NAME -->
          <div class="input-group">
            <label>Full Name / Business Name</label>
            <input
              type="text"
              v-model="form.name"
              required
              minlength="5"
              placeholder="e.g. Innocent Kalwanda or Acme Sports Ltd"
              @blur="touched.name = true"
            />
            <span v-if="touched.name && errors.name" class="error-text">{{ errors.name }}</span>
          </div>

          <!-- PHONE: +254 fixed prefix, user enters exactly 9 digits -->
          <div class="input-group">
            <label>Phone Number</label>
            <div class="phone-field">
              <span class="phone-prefix" aria-hidden="true">+254</span>
              <input
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="9"
                class="phone-national"
                :value="form.phone"
                required
                placeholder="712345678"
                autocomplete="tel-national"
                @input="onPhoneNationalInput"
                @blur="touched.phone = true"
              />
            </div>
            <span class="phone-hint">Enter 9 digits only (e.g. 712345678). This number will be used by customers to contact you.</span>
            <span v-if="touched.phone && errors.phone" class="error-text">{{ errors.phone }}</span>
          </div>

          <!-- LOCATION -->
          <div class="input-group">
            <label>Pickup / Meeting Location</label>
            <input type="text" v-model="form.location" required placeholder="Nairobi CBD, Karen…" />
          </div>

          <!-- SOCIAL HANDLES -->
          <div class="input-group">
            <label>Social Handle (optional)</label>
            <p class="social-reach-hint">Adding a social handle will increase your customer reach.</p>
            <div class="social-type-row">
              <button
                type="button"
                class="social-type-btn"
                :class="{ active: form.socialType === 'instagram' }"
                @click="form.socialType = 'instagram'; form.social = ''"
                aria-label="Add Instagram"
              >
                <i class="fa-brands fa-instagram"></i>
                <span>Instagram</span>
              </button>
              <button
                type="button"
                class="social-type-btn"
                :class="{ active: form.socialType === 'whatsapp' }"
                @click="form.socialType = 'whatsapp'; form.social = ''"
                aria-label="Add WhatsApp"
              >
                <i class="fa-brands fa-whatsapp"></i>
                <span>WhatsApp</span>
              </button>
            </div>
            <template v-if="form.socialType === 'instagram'">
              <input
                type="text"
                v-model="form.social"
                placeholder="Your IG shop username (e.g. myshop_ke)"
                class="social-input"
              />
            </template>
            <template v-else-if="form.socialType === 'whatsapp'">
              <input
                type="tel"
                v-model="form.social"
                placeholder="+254 7XX XXX XXX"
                class="social-input"
              />
            </template>
          </div>

          <!-- PROFILE PICTURE -->
          <div class="input-group file-group">
            <label>Profile Picture</label>
            <div class="file-row">
              <input
                id="file-input"
                ref="fileInput"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                @change="uploadImage"
              />
              <div
                class="thumb-preview"
                :style="previewUrl ? { backgroundImage: 'url(' + previewUrl + ')' } : {}"
                role="img"
                :aria-label="form.name ? form.name : 'Profile preview'"
              ></div>
            </div>
            <small class="hint">PNG, JPG up to 3MB. Optional — you can upload later.</small>
          </div>

          <label class="seller-legal-consent">
            <input v-model="agreedToPolicies" type="checkbox" :disabled="loading" required />
            <span class="seller-legal-consent-text">
              I agree to the
              <router-link :to="{ name: 'BuyerTerms' }" class="consent-link" @click.stop>IPA Buyer Terms</router-link>,
              <router-link :to="{ name: 'SellerTerms' }" class="consent-link" @click.stop>Seller Terms</router-link>,
              and
              <router-link :to="{ name: 'CommunityGuidelines' }" class="consent-link" @click.stop>
                Community Guidelines
              </router-link>.
            </span>
          </label>

          <div class="cta-row">
            <button class="btn primary" type="submit" :disabled="loading">
              {{ loading ? "Preparing..." : "Continue" }}
            </button>
            <button class="btn ghost" type="button" @click="resetForm" :disabled="loading">Reset</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Confirmation Modal - IMPROVED LAYOUT -->
    <transition name="fade">
      <div v-if="showModal" class="modal-overlay" @click.self="closePreview">
        <transition name="scale">
          <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="modal-title">
            <header class="modal-header">
              <div class="modal-title-block">
                <h3 id="modal-title">Preview your seller profile</h3>
                <p class="modal-sub">Confirm the details below before creating your seller account.</p>
              </div>
              <button class="close-x" @click="closePreview" aria-label="Close preview">✕</button>
            </header>

            <main class="modal-body">
              <!-- Two-column layout with better spacing -->
              <div class="modal-grid">
                <!-- LEFT: Profile Preview -->
                <div class="modal-left">
                  <div class="profile-section">
                    <h4 class="section-title">
                      <i class="fa-solid fa-user section-icon"></i>
                      Profile Preview
                    </h4>
                    <div class="profile-preview">
                      <div
                        class="profile-pic"
                        :style="previewUrl ? { backgroundImage: 'url(' + previewUrl + ')' } : {}"
                      ></div>
                      <div class="profile-details">
                        <h5>{{ form.name || "—" }}</h5>
                        <div class="detail-row">
                          <i class="fa-solid fa-phone"></i>
                          <span>{{ phoneDisplayInternational || "—" }}</span>
                        </div>
                        <div class="detail-row">
                          <i class="fa-solid fa-location-dot"></i>
                          <span>{{ form.location || "—" }}</span>
                        </div>
                        <div class="detail-row" v-if="form.social && form.socialType">
                          <i :class="form.socialType === 'instagram' ? 'fa-brands fa-instagram' : 'fa-brands fa-whatsapp'"></i>
                          <span>{{ form.socialType === 'instagram' ? '@' + form.social : form.social }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="terms-note">
                    <div class="note-icon">
                      <i class="fa-solid fa-circle-info"></i>
                    </div>
                    <div class="note-content">
                      <p>
                        By creating a seller account you confirm you have read and accept the
                        <router-link :to="{ name: 'BuyerTerms' }" class="link">IPA Buyer Terms</router-link>,
                        <router-link :to="{ name: 'SellerTerms' }" class="link">Seller Terms</router-link>, and
                        <router-link :to="{ name: 'CommunityGuidelines' }" class="link">Community Guidelines</router-link>.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- RIGHT: Summary & Actions -->
                <div class="modal-right">
                  <div class="summary-section">
                    <h4 class="section-title">
                      <i class="fa-solid fa-file-lines section-icon"></i>
                      Summary
                    </h4>
                    
                    <div class="summary-grid">
                      <div class="summary-item">
                        <span class="item-label">Name</span>
                        <span class="item-value">{{ form.name || "—" }}</span>
                      </div>
                      <div class="summary-item">
                        <span class="item-label">Phone</span>
                        <span class="item-value">{{ phoneDisplayInternational || "—" }}</span>
                      </div>
                      <div class="summary-item">
                        <span class="item-label">Location</span>
                        <span class="item-value">{{ form.location || "—" }}</span>
                      </div>
                      <div class="summary-item" v-if="form.social && form.socialType">
                        <span class="item-label">{{ form.socialType === 'instagram' ? 'Instagram' : 'WhatsApp' }}</span>
                        <span class="item-value">{{ form.socialType === 'instagram' ? '@' + form.social : form.social }}</span>
                      </div>
                      <div class="summary-item">
                        <span class="item-label">Profile Picture</span>
                        <span class="item-value">{{ form.image ? "Uploaded" : "Default" }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="actions-section">
                    <!-- Buttons replaced by success state after creation -->
                    <div v-if="!created">
                      <div class="action-buttons">
                        <button class="btn-primary confirm-btn" @click="confirmSignup" :disabled="submitting">
                          <i class="fa-solid fa-check"></i>
                          {{ submitting ? "Creating Account..." : "Confirm & Create Account" }}
                        </button>
                        <button class="btn-secondary" @click="closePreview" :disabled="submitting">
                          <i class="fa-solid fa-times"></i>
                          Cancel
                        </button>
                      </div>
                      <p class="action-hint">
                        <i class="fa-solid fa-clock"></i>
                        Account setup takes less than 10 seconds
                      </p>
                    </div>

                    <div v-else class="success-state">
                      <div class="success-icon">
                        <i class="fa-solid fa-circle-check"></i>
                      </div>
                      <div class="success-content">
                        <h5>Account Created Successfully!</h5>
                        <p class="success-message">Your seller account is now active. Start listing your products to reach thousands of buyers.</p>
                        <button class="btn-primary dashboard-btn" @click="goToDashboard">
                          <i class="fa-solid fa-rocket"></i>
                          Go to Seller Dashboard
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </transition>
      </div>
    </transition>

    <ThirdpartyFooter />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import ThirdpartyFooter from "./Footer.vue";
import { buildApiUrl } from "@/config/api";
import { useAuthStore } from "@/authen/auth";
import globalToastMixin from "@/mixins/globalToastMixin";
import {
  AUTH_REDIRECT_PATHS,
  buildSignInLocation,
  navigateToSignIn,
} from "@/utils/authRedirect";

/** Maximum profile image size (3MB) — must match backend MAX_SELLER_PROFILE_SIZE */
const MAX_PROFILE_IMAGE_BYTES = 3 * 1024 * 1024;

/**
 * SellerSignup — Third-party seller registration page.
 *
 * Lets an authenticated customer create a seller account: name, phone (+254 + 9 digits),
 * location, optional social handle (Instagram or WhatsApp), and optional profile picture.
 * Submits to POST /seller/create; on success shows a confirmation modal and option to
 * go to the seller dashboard.
 *
 * @module SellerSignup
 * @component
 */
export default {
  name: "SellerSignup",
  mixins: [globalToastMixin],
  components: { MainNavbar, ThirdpartyFooter },

  data() {
    return {
      form: {
        name: "",
        /** National significant number only: exactly 9 digits (e.g. 712345678), without +254 */
        phone: "",
        location: "",
        social: "",
        socialType: "", // 'instagram' | 'whatsapp'
        image: null,
      },
      touched: { name: false, phone: false },
      showModal: false,
      loading: false,
      submitting: false,
      created: false,
      previewUrl: "",
      /** Placeholder when no image selected; use empty string to avoid broken request to filesystem path */
      fallbackImagePath: "",
      /** Required before opening the preview modal — buyer, seller, and community policies */
      agreedToPolicies: false,
    };
  },

  computed: {
    /** Validation messages keyed by field name. */
    errors() {
      const e = {};
      const name = (this.form.name || "").trim();
      if (name.length > 0 && name.length < 5) {
        e.name = "Full name / business name must be at least 5 characters.";
      }
      const digits = (this.form.phone || "").replace(/\D/g, "");
      if (digits.length > 0) {
        if (digits.length !== 9) {
          e.phone = "Enter exactly 9 digits after +254.";
        } else if (!/^7\d{8}$/.test(digits)) {
          e.phone = "Kenyan mobile numbers use 9 digits starting with 7 (e.g. 712345678).";
        }
      }
      return e;
    },

    /** E.164-style value for display and API: +254712345678 */
    phoneDisplayInternational() {
      const digits = (this.form.phone || "").replace(/\D/g, "");
      if (digits.length !== 9 || !/^7\d{8}$/.test(digits)) return "";
      return `+254${digits}`;
    },

    /** True when all required fields are present and valid. */
    formValid() {
      const name = (this.form.name || "").trim();
      const digits = (this.form.phone || "").replace(/\D/g, "");
      const location = (this.form.location || "").trim();
      return (
        name.length >= 5 &&
        digits.length === 9 &&
        /^7\d{8}$/.test(digits) &&
        location.length > 0 &&
        !this.errors.name &&
        !this.errors.phone
      );
    },

    /** First validation message to show, or null if valid. */
    firstValidationMessage() {
      if (this.errors.name) return this.errors.name;
      if (this.errors.phone) return this.errors.phone;
      const location = (this.form.location || "").trim();
      if (!location) return "Pickup / meeting location is required.";
      return null;
    },
  },

  beforeMount() {
    const auth = useAuthStore();
    auth.checkToken();
    if (!auth.isAuthenticated) {
      this.$router.replace(buildSignInLocation(this.$route.fullPath));
    }
  },

  mounted() {
    this.previewUrl = this.fallbackImagePath;
  },

  methods: {
    /** Digits only, max 9, for the national part after +254 */
    onPhoneNationalInput(e) {
      const raw = (e.target?.value || "").replace(/\D/g, "").slice(0, 9);
      this.form.phone = raw;
    },

    /** Dispatch a global toast (handled by App.vue ToastContainer). */
    /**
     * Handle profile image file selection. Validates size and type; revokes previous blob URL.
     * @param {Event} e - change event from file input
     */
    uploadImage(e) {
      const file = e?.target?.files?.[0];
      if (!file) return;

      if (this.previewUrl && this.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.previewUrl);
      }

      // Validate MIME type on the client so we never send an invalid file to the API
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        this.showToast(
          "Unsupported image type",
          "Please upload a JPG, PNG, GIF, or WebP image.",
          "warning"
        );
        // Clear any previously selected image and preview
        this.form.image = null;
        this.previewUrl = this.fallbackImagePath;
        const input = this.$refs.fileInput;
        if (input) input.value = "";
        return;
      }

      if (file.size > MAX_PROFILE_IMAGE_BYTES) {
        this.showToast("Image too large", "Maximum size is 3MB.", "warning");
        return;
      }

      this.form.image = file;
      this.previewUrl = URL.createObjectURL(file);
    },

    /** Validate form and open confirmation modal, or show a single validation toast. */
    openPreview() {
      this.touched.name = true;
      this.touched.phone = true;
      if (!this.agreedToPolicies) {
        this.showToast(
          "Policies required",
          "Please agree to the IPA Buyer Terms, Seller Terms, and Community Guidelines.",
          "warning"
        );
        return;
      }
      if (!this.formValid) {
        const msg = this.firstValidationMessage || "Please fill all required fields correctly.";
        this.showToast("Validation", msg, "warning");
        return;
      }
      this.showModal = true;
    },

    /** Close modal unless a submit is in progress. */
    closePreview() {
      if (this.submitting) return;
      this.showModal = false;
    },

    /** Reset form and preview; revoke blob URL if present. */
    resetForm() {
      if (this.previewUrl && this.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.form = { name: "", phone: "", location: "", social: "", socialType: "", image: null };
      this.touched = { name: false, phone: false };
      this.agreedToPolicies = false;
      this.previewUrl = this.fallbackImagePath;
      const input = this.$refs.fileInput;
      if (input) input.value = "";
    },

    /**
     * Submit seller registration to API. Shows toasts on success or error.
     * On success, sets created so modal shows success state.
     */
    async confirmSignup() {
      try {
        this.submitting = true;

        const token = localStorage.getItem("access_token");
        if (!token) {
          this.showToast("Authentication required", "You must be logged in first.", "warning");
          navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.SELLER_SIGNUP);
          return;
        }

        // Safety net: if somehow an invalid file type is present, stop on the client
        if (this.form.image) {
          const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
          if (!allowedTypes.includes(this.form.image.type)) {
            this.showToast(
              "Unsupported image type",
              "Please upload a JPG, PNG, GIF, or WebP image.",
              "warning"
            );
            return;
          }
        }

        const socialValue =
          this.form.socialType && this.form.social
            ? this.form.socialType === "instagram"
              ? `Instagram: ${String(this.form.social).trim().replace(/^@/, "")}`
              : `WhatsApp: ${String(this.form.social).replace(/\s/g, "")}`
            : "";

        const formData = new FormData();
        formData.append("name", (this.form.name || "").trim());
        formData.append("phone", this.phoneDisplayInternational);
        formData.append("location", (this.form.location || "").trim());
        formData.append("social", socialValue);
        if (this.form.image) {
          formData.append("image", this.form.image);
        }

        let res;
        try {
          res = await fetch(buildApiUrl("/seller/create"), {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });
        } catch (networkErr) {
          this.showToast("Network error", "Check your connection and try again.", "error");
          return;
        }

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          const message = this.getApiErrorMessage(json, res.status, "Signup failed");
          this.showToast(this.getErrorToastTitle(res.status), message, "error");
          return;
        }

        this.showToast(
          "Account created",
          "Your seller account has been created. You can add a profile photo later from your dashboard.",
          "success"
        );
        this.created = true;
      } catch (err) {
        console.error("Signup failed:", err);
        this.showToast("Signup failed", err.message || "Something went wrong. Please try again.", "error");
      } finally {
        this.submitting = false;
      }
    },

    /**
     * Build a single user-facing message from API error response.
     * @param {Object} json - Response body (may have detail: string | array)
     * @param {number} statusCode - HTTP status
     * @param {string} fallback - Default message
     * @returns {string}
     */
    getApiErrorMessage(json, statusCode, fallback) {
      const d = json?.detail;
      if (typeof d === "string") return d;
      if (Array.isArray(d) && d.length) {
        return d.map((e) => e.msg ?? e.message ?? JSON.stringify(e)).join(". ");
      }
      if (statusCode === 401) return "Please log in and try again.";
      if (statusCode === 403) return "You don't have permission to do this.";
      if (statusCode === 404) return "The requested resource was not found.";
      if (statusCode >= 500) return "Server error. Please try again later.";
      return fallback || "Something went wrong. Please try again.";
    },

    /** Toast title from HTTP status for error toasts. */
    getErrorToastTitle(statusCode) {
      const titles = { 401: "Login required", 403: "Not allowed", 400: "Invalid request", 404: "Not found" };
      return titles[statusCode] ?? (statusCode >= 500 ? "Server error" : "Error");
    },

    /** Navigate to seller dashboard after successful signup. */
    goToDashboard() {
      this.$router.push("/ipromiseafrica/resale/seller-dashboard");
    },
  },
};
</script>

<style scoped>
/* ================= BASE STYLES ================= */
.signup-container {
  min-height: 100vh;
  width: 100%;
  background: var(--background-color1);
  display: flex;
  flex-direction: column;
  font-family: "Inter", "Poppins", system-ui, -apple-system, sans-serif;
  padding-top: 80px;
}

.signup-container :deep(.thirdparty-footer) {
  margin-top: 0 !important;
  padding-top: 0 !important;
  align-self: stretch;
}

.phone-field {
  display: flex;
  align-items: stretch;
  gap: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border-color-hover1, rgba(0, 0, 0, 0.12));
  background: var(--background-color2, #fff);
}

.phone-prefix {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 12px;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-color1);
  background: var(--background-color3, rgba(0, 0, 0, 0.04));
  border-right: 1px solid var(--border-color-hover1, rgba(0, 0, 0, 0.12));
}

.phone-national {
  flex: 1;
  min-width: 0;
  border: none !important;
  background: transparent;
  padding: 12px 14px;
  font-size: 1rem;
  color: var(--text-color1);
}

.input-group .phone-national:focus {
  outline: none;
  border: none;
  box-shadow: none;
}

.phone-field:focus-within {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 2px rgba(128, 0, 0, 0.08);
}

.center-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 36px 16px;
}

/* ================= SIGNUP CARD ================= */
.signup-card {
  background: var(--background-color2);
  padding: 30px;
  width: 100%;
  max-width: 500px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.title {
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 8px;
}

.subtitle {
  text-align: center;
  font-size: 14px;
  color: var(--text-color2);
  margin-bottom: 24px;
}

/* ================= FORM STYLES ================= */
.signup-form {
  display: grid;
  gap: 16px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color1);
}

.input-group input {
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 14px;
  transition: all 0.2s;
}

.input-group input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.error-text {
  font-size: 12px;
  color: #ef4444;
  margin-top: 2px;
}

.phone-hint {
  font-size: 12px;
  color: var(--text-color2);
  margin-top: 2px;
}

.social-reach-hint {
  font-size: 12px;
  color: var(--text-color2);
  margin: 0 0 8px 0;
}

.social-type-row {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.social-type-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: var(--background-color1);
  color: var(--text-color2);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.social-type-btn i {
  font-size: 18px;
}

.social-type-btn:first-child.active {
  border-color: #E1306C;
  color: #E1306C;
  background: rgba(225, 48, 108, 0.08);
}

.social-type-btn:last-child.active {
  border-color: #25D366;
  color: #25D366;
  background: rgba(37, 211, 102, 0.08);
}

.social-type-btn:hover {
  border-color: var(--border-color-hover);
  color: var(--text-color1);
}

.social-input {
  margin-top: 4px;
}

/* File upload */
.file-group .file-row {
  display: flex;
  gap: 16px;
  align-items: center;
}

.thumb-preview {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  background-size: cover;
  background-position: center;
  background-color: var(--background-color3);
  border: 2px dashed rgba(128, 128, 128, 0.4);
}

.hint {
  font-size: 12px;
  color: var(--text-color2);
  margin-top: 6px;
}

.seller-legal-consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 18px;
  margin-bottom: 4px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-color1);
}

.seller-legal-consent input[type="checkbox"] {
  margin-top: 3px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  accent-color: var(--border-color-hover);
}

.seller-legal-consent-text {
  flex: 1;
}

.consent-link {
  color: var(--border-color-hover);
  text-decoration: none;
  font-weight: 600;
}

.consent-link:hover {
  text-decoration: underline;
}

/* Buttons */
.cta-row {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.btn {
  padding: 12px 24px;
  border-radius: 10px;
  cursor: pointer;
  border: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s;
}

.btn.primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
  flex: 1;
}

.btn.primary:hover:not(:disabled) {
  opacity: 1;
  transform: none;
}

.btn.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn.ghost {
  background: transparent;
  border: 1px solid #e5e7eb;
  color: var(--text-color1);
}

.btn.ghost:hover {
  background: #f9fafb;
}

/* ================= MODAL - IMPROVED LAYOUT ================= */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.modal-card {
  background: var(--background-color2);
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  animation: modalSlideUp 0.3s ease;
  border: 1px solid var(--background-color3);
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--background-color3);
}

.modal-title-block h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-color1);
  margin: 0;
}

.modal-sub {
  font-size: 14px;
  color: var(--text-color2);
  margin: 4px 0 0;
}

.close-x {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-color2);
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-x:hover {
  background: var(--background-color3);
}

/* Modal Body - NO SCROLLING */
.modal-body {
  padding: 0;
  overflow: visible;
  flex: 1;
}

.modal-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 100%;
  min-height: 500px;
}

/* Left Column */
.modal-left {
  padding: 24px;
  border-right: 1px solid var(--background-color3);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Right Column */
.modal-right {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Section Titles */
.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color1);
  margin-bottom: 16px;
}

.section-icon {
  color: var(--border-color-hover);
  font-size: 14px;
}

/* Profile Preview */
.profile-preview {
  display: flex;
  gap: 20px;
  align-items: center;
}

.profile-pic {
  width: 100px;
  height: 100px;
  border-radius: 12px;
  background-size: cover;
  background-position: center;
  background-color: var(--background-color3);
  border: 3px solid var(--background-color2);
  box-shadow: 0 4px 12px var(--box-shadow);
}

.profile-details {
  flex: 1;
}

.profile-details h5 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-color1);
  margin: 0 0 12px 0;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text-color2);
}

.detail-row i {
  width: 16px;
  color: var(--border-color-hover);
}

/* Terms Note */
.terms-note {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: var(--background-color3);
  border-radius: 10px;
  border-left: 4px solid var(--border-color-hover);
}

.note-icon {
  color: var(--border-color-hover);
  font-size: 18px;
}

.note-content {
  flex: 1;
}

.note-content p {
  font-size: 13px;
  color: var(--text-color2);
  margin: 0;
  line-height: 1.5;
}

.note-content .link {
  color: var(--border-color-hover);
  text-decoration: none;
  font-weight: 500;
}

.note-content .link:hover {
  text-decoration: underline;
}

/* Summary Section */
.summary-grid {
  display: grid;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--background-color3);
}

.summary-item:last-child {
  border-bottom: none;
}

.item-label {
  font-size: 14px;
  color: var(--text-color2);
}

.item-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color1);
}

/* Actions Section */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn-primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: none;
  padding: 14px 20px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary.confirm-btn:hover:not(:disabled) {
  opacity: 1;
  transform: none;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--background-color3);
  color: var(--text-color1);
  padding: 14px 20px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-secondary:hover {
  background: var(--background-color3);
}

.action-hint {
  font-size: 13px;
  color: var(--text-color2);
  text-align: center;
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 20px 0;
}

.success-icon {
  font-size: 48px;
  color: var(--border-color-hover);
  margin-bottom: 16px;
}

.success-content h5 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-color1);
  margin: 0 0 8px 0;
}

.success-message {
  font-size: 14px;
  color: var(--text-color2);
  margin-bottom: 20px;
  line-height: 1.5;
}

.dashboard-btn {
  width: 100%;
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: 2px solid var(--border-color-hover);
  padding: 14px 20px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.dashboard-btn:hover {
  opacity: 0.92;
  transform: translateY(-1px);
  color: var(--text-color3);
  background: var(--border-color-hover);
}

/* ================= RESPONSIVE ================= */
@media (max-width: 900px) {
  .modal-grid {
    grid-template-columns: 1fr;
    max-height: none;
  }
  
  .modal-left {
    border-right: none;
    border-bottom: 1px solid var(--background-color3);
  }
  
  .modal-card {
    max-height: 95vh;
  }
}

@media (max-width: 600px) {
  .signup-card {
    padding: 20px;
  }
  
  .profile-preview {
    flex-direction: column;
    text-align: center;
  }
  
  .detail-row {
    justify-content: center;
  }
  
  .modal-header {
    padding: 16px 20px;
  }
  
  .modal-left, .modal-right {
    padding: 20px;
  }
  
  .btn, .btn-primary, .btn-secondary {
    padding: 12px 16px;
    font-size: 13px;
  }
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.scale-enter-active, .scale-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.scale-enter-from {
  transform: scale(0.95);
  opacity: 0;
}

.scale-leave-to {
  transform: scale(0.95);
  opacity: 0;
}
</style>